import {
  CircleAlert,
  CircleCheck,
  CircleDashed,
  Clock,
  CopyCheck,
  Hourglass,
  Sparkles,
  Upload,
  type LucideIcon,
} from "lucide-react";
import { TFunction } from "i18next";
import { BadgeVariant } from "components/ui/badge";
import { ManuscriptProcessingStatus } from "integrations/firebase/collections/manuscriptProcessingReporting";

interface StatusPresentation {
  // Literal keys keep the labels visible to the i18next extractor.
  label: (t: TFunction) => string;
  Icon: LucideIcon;
  badgeVariant: BadgeVariant;
  tileIconClassName: string;
  timelineDotClassName: string;
}

export const STATUS_PRESENTATION: Record<
  ManuscriptProcessingStatus,
  StatusPresentation
> = {
  // Matched to a product but not submitted: the organisation has not opted in.
  matched: {
    label: (t) =>
      t("settings:organisation.manuscriptProcessing.status.matched"),
    Icon: CircleDashed,
    badgeVariant: "secondary",
    tileIconClassName: "text-secondary-400",
    timelineDotClassName: "bg-secondary-300",
  },
  queued: {
    label: (t) => t("settings:organisation.manuscriptProcessing.status.queued"),
    Icon: Clock,
    badgeVariant: "secondary",
    tileIconClassName: "text-secondary-400",
    timelineDotClassName: "bg-secondary-300",
  },
  uploading: {
    label: (t) =>
      t("settings:organisation.manuscriptProcessing.status.uploading"),
    Icon: Upload,
    badgeVariant: "default",
    tileIconClassName: "text-primary-600",
    timelineDotClassName: "bg-primary-600",
  },
  processing: {
    label: (t) =>
      t("settings:organisation.manuscriptProcessing.status.processing"),
    Icon: Sparkles,
    badgeVariant: "default",
    tileIconClassName: "text-primary-600",
    timelineDotClassName: "bg-primary-600",
  },
  complete: {
    label: (t) =>
      t("settings:organisation.manuscriptProcessing.status.complete"),
    Icon: CircleCheck,
    badgeVariant: "success",
    tileIconClassName: "text-badge-success",
    timelineDotClassName: "bg-badge-success",
  },
  error: {
    label: (t) => t("settings:organisation.manuscriptProcessing.status.error"),
    Icon: CircleAlert,
    badgeVariant: "error",
    tileIconClassName: "text-badge-error",
    timelineDotClassName: "bg-badge-error",
  },
  // Content already submitted for this product, so nothing was sent to Essence.
  skipped_duplicate: {
    label: (t) =>
      t("settings:organisation.manuscriptProcessing.status.skippedDuplicate"),
    Icon: CopyCheck,
    badgeVariant: "secondary",
    tileIconClassName: "text-secondary-400",
    timelineDotClassName: "bg-secondary-300",
  },
  unmatched: {
    label: (t) =>
      t("settings:organisation.manuscriptProcessing.status.unmatched"),
    Icon: Hourglass,
    badgeVariant: "warning",
    tileIconClassName: "text-system-warning",
    timelineDotClassName: "bg-system-warning",
  },
};

export const getStatusPresentation = (
  status: string,
): StatusPresentation | undefined =>
  STATUS_PRESENTATION[status as ManuscriptProcessingStatus];
